/**
 * Zod boundary schemas + view types for Staffing Requests (docs/specs/staffing-requests.md)
 * — a request to Add, Replace, Remove or Swap a Resource on a Solution's Pod.
 */

import { z } from "zod";
import type { PositionDisciplines } from "@/lib/position-label";
import { type Composition, SEAT_ROLES, type SeatRole } from "@/lib/staffing-change";
import type { PlanResourcingStatus } from "@/schemas/resource-plans";
import type { RequestPriorityTag } from "@/schemas/staffing-request-priorities";

export const STAFFING_REQUEST_KINDS = ["Add", "Replace", "Remove", "Swap"] as const;
export type StaffingRequestKind = (typeof STAFFING_REQUEST_KINDS)[number];

export const STAFFING_REQUEST_STATUSES = ["Pending", "Approved", "Rejected", "Withdrawn"] as const;
export type StaffingRequestStatus = (typeof STAFFING_REQUEST_STATUSES)[number];

const id = z.string().trim().min(1);
const optionalId = id.nullable().optional();

const common = {
  solutionId: id,
  priorityId: optionalId,
  neededBy: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, { message: "neededBy must be YYYY-MM-DD" })
    .nullable()
    .optional(),
  reason: z
    .string()
    .trim()
    .min(1, { message: "Say why the change is needed" })
    .max(2000, { message: "Keep the reason under 2,000 characters" }),
};

/** POST /api/staffing-requests */
export const createStaffingRequestSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("Add"),
    ...common,
    role: z.enum(SEAT_ROLES),
    disciplineId: optionalId,
    suggestedResourceId: optionalId,
  }),
  z.object({
    kind: z.literal("Replace"),
    ...common,
    targetAssignmentId: id,
    disciplineId: optionalId,
    suggestedResourceId: optionalId,
  }),
  z.object({
    kind: z.literal("Remove"),
    ...common,
    targetAssignmentId: id,
  }),
  // Swap: move the Resource on `sourceAssignmentId` (another Solution's Pod) into `role`
  // here. `targetAssignmentId` is the seat's incumbent, or null for an open seat. The
  // Resource is derived from the source assignment server-side — never sent separately.
  z.object({
    kind: z.literal("Swap"),
    ...common,
    role: z.enum(SEAT_ROLES),
    targetAssignmentId: id.nullable(),
    sourceAssignmentId: id,
    disciplineId: optionalId,
  }),
]);
export type CreateStaffingRequestInput = z.infer<typeof createStaffingRequestSchema>;

/**
 * POST /api/staffing-requests/:id/approve — resourceId is required for Add / Replace. A
 * Swap's Resource is fixed; a resourceId naming anyone else is refused.
 */
export const approveStaffingRequestSchema = z.object({
  resourceId: optionalId,
  note: z.string().trim().max(2000).optional(),
});

/** POST /api/staffing-requests/:id/reject */
export const rejectStaffingRequestSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, { message: "A rejection needs a reason" })
    .max(2000, { message: "Keep the reason under 2,000 characters" }),
});

/** GET /api/staffing-requests query string. */
export const listStaffingRequestsQuerySchema = z.object({
  statuses: z.array(z.enum(STAFFING_REQUEST_STATUSES)).optional(),
  kinds: z.array(z.enum(STAFFING_REQUEST_KINDS)).optional(),
  priorityIds: z.array(id).optional(),
  cursor: z.string().optional(),
  direction: z.enum(["next", "prev"]).optional(),
  limit: z.number().int().min(1).max(100).optional(),
});
export type ListStaffingRequestsQuery = z.infer<typeof listStaffingRequestsQuerySchema>;

// ── Views ────────────────────────────────────────────────────────────────────

export interface NamedRef {
  id: string;
  name: string;
}

export interface PodMemberView {
  assignmentId: string;
  resourceId: string;
  resourceName: string;
  role: SeatRole;
}

export interface PodView {
  composition: Composition;
  members: PodMemberView[];
  /** What each seat is for (R1 → SA …), so seats read as more than "R1". */
  positionDisciplines: PositionDisciplines;
}

export interface StaffingRequestView {
  id: string;
  kind: StaffingRequestKind;
  status: StaffingRequestStatus;
  role: SeatRole;
  solution: NamedRef;
  account: NamedRef | null;
  /**
   * Replace / Remove / Swap: who the request is about (snapshotted at request time). Null
   * for an Add, and for a Swap into an open seat.
   */
  target: { assignmentId: string | null; resource: NamedRef | null } | null;
  /** Swap: the seat the moved Resource leaves on another Solution's Pod. */
  source: { assignmentId: string | null; solution: NamedRef | null; role: SeatRole | null } | null;
  discipline: { id: string; code: string; name: string } | null;
  suggestedResource: NamedRef | null;
  fulfilledResource: NamedRef | null;
  priority: RequestPriorityTag | null;
  neededBy: string | null;
  reason: string;
  requestedBy: NamedRef | null;
  decidedBy: NamedRef | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
  /** What each of the Solution's seats is for, so the seat reads as "R2 · SA", not "R2". */
  positionDisciplines: PositionDisciplines;
  /** The viewer may approve / reject this request (RM, while Pending). */
  canDecide: boolean;
  /** The viewer may withdraw this request (its requester, while Pending). */
  canWithdraw: boolean;
}

export interface StaffingRequestDetailView extends StaffingRequestView {
  /** The Solution's Pod as it stands now — what approval would change. */
  pod: PodView;
}

export interface StaffingRequestPage {
  data: StaffingRequestView[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

/** A Solution the viewer may raise a request on, with its Pod — feeds the request form. */
export interface StaffingSolutionOption {
  id: string;
  name: string;
  accountName: string | null;
  pod: PodView;
  /** Pod members that already have a Pending Replace / Remove — not selectable again. */
  pendingTargetAssignmentIds: string[];
}

export interface StaffingResourceOption {
  id: string;
  name: string;
  disciplines: Array<{ code: string; name: string }>;
}

/** One of a swap candidate's current seats on another Solution's Pod. */
export interface SwapSeatOption {
  assignmentId: string;
  solution: NamedRef;
  role: SeatRole;
  /** A Pending Swap already moves this person out of this seat — not selectable again. */
  pendingMove: boolean;
}

/**
 * GET /api/staffing-requests/swap-candidates — a Resource who could be moved into a seat
 * on the Solution, with the seats they hold on OTHER Pods. Narrow, like
 * StaffingResourceOption: name, Discipline and seats only.
 */
export interface StaffingSwapCandidate extends StaffingResourceOption {
  seats: SwapSeatOption[];
}

/** An unfilled Plan Line — a Labor Category the plan has nobody on yet. */
export interface OpenRoleView {
  lineId: string;
  laborCategory: string;
  /** The client-facing title, when the plan uses one (RP-6.3). */
  clientLaborCategory: string | null;
  workstream: string | null;
  /** The Pod seat the line maps to, if any. */
  position: SeatRole | null;
}

/**
 * GET /api/staffing-requests/open-roles — one plan still being staffed (Resourcing status
 * not Staffed) and its open roles.
 */
export interface OpenRolePlanGroup {
  plan: {
    id: string;
    name: string;
    /** The sales status. */
    status: string;
    resourcingStatus: PlanResourcingStatus | null;
  };
  solution: NamedRef;
  account: NamedRef | null;
  /** The viewer may raise a Swap on this plan's Solution. */
  canRequest: boolean;
  /** Every Plan Line on the plan, open or not. */
  lineCount: number;
  /** The lines with nobody on them. */
  roles: OpenRoleView[];
}
